import {
  GameSessionModelData,
  GameInfoData,
  debounce,
  SessionSourceInfo,
  GameReleaseData,
} from "@citychallenge/common";
import {
  GameEntity,
  LanguageEntity,
  SessionEntity,
  SessionUpdateDto,
  SessionUpdateDtoPlatformEnum,
} from "@citychallenge/api-public";
import { LoggerService } from "../logger";
import { ApiService } from "../api";
import { StorageService } from "../storage/storage-service";
import { generateRandomId } from "@citychallenge/common";
import { ServiceInterface } from "../service.interface";
import { ReachabilityService } from "../reachability";
import {
  observable,
  action,
  reaction,
  computed,
  runInAction,
  IReactionDisposer,
} from "mobx";
import { UserService } from "../user";
import { FileService } from "../file";
import { I18nService } from "../i18n";
import { DeviceService } from "../device";
import { createContext } from "react";
import { GameService } from "../game";
import { Environment } from "../../environment";
import { GameSessionModel } from "../../player/game-session/game-session.model";
import { fetchKeepalive } from "../../util/fetch-keepalive";
import { nextSyncStamp } from "./sync-stamp";
import { SyncQueueRunner } from "./sync-queue-runner";

interface StartGameEntity {
  id: string;
  release: GameReleaseData;
  distanceInMeters?: number;
  activeSession?: GameSessionModel;
  hideLeaderboard?: boolean;
  locale?: string;
  defaultLanguage?: LanguageEntity;
}

export interface SessionUpdate extends SessionUpdateDto {
  gameData?: GameSessionModelData;
}

export interface SessionUpdateSync
  extends Omit<SessionUpdate, "platform" | "started" | "finished"> {
  started: Date;
  finished?: Date;
}

export interface SessionStartOptions {
  qrCodeHit?: string;
  source?: SessionSourceInfo;
  lang?: string;
  divisionId?: string;
  incomingRef?: string;
  variables?: { [key: string]: string };
}

export interface FetchActiveSessionOptions {
  /**
   * The `releaseVersion` of the latest published release of the game. Used
   * together with `restartIfOutdated` to determine whether an existing
   * session should be discarded.
   */
  latestReleaseVersion?: number;
  /**
   * When `true`, an existing session is discarded (and `undefined` returned)
   * if its stored `releaseVersion` is older than `latestReleaseVersion`. This
   * is driven by the division-level "Show latest story version" setting.
   */
  restartIfOutdated?: boolean;
}

export interface SessionQueueItem {
  syncId: string;
  update: SessionUpdateSync;
}

export interface SessionQueue {
  [key: string]: SessionQueueItem;
}

export const SessionServiceContext = createContext<SessionService>(null as any);

/**
 * Engaged-time step (seconds) between activity-metric syncs. The metrics
 * reaction keys off the active-time bucket so it fires roughly once per this
 * many seconds of engaged time, and never while the session is idle.
 */
const SESSION_METRICS_SYNC_STEP = 15;

export class SessionService implements ServiceInterface {
  public context = SessionServiceContext;

  private platform: SessionUpdateDtoPlatformEnum =
    Environment.OS as SessionUpdateDtoPlatformEnum;

  private get deviceConstants() {
    return {
      platform: this.platform,
      systemVersion: Environment.systemVersion,
      deviceId: Environment.deviceId,
    };
  }
  constructor(
    private logger: LoggerService,
    private apiService: ApiService,
    private reachabilityService: ReachabilityService,
    private storageService: StorageService,
    private userService: UserService,
    private i18nService: I18nService,
    private deviceService: DeviceService,
    private fileService: FileService,
    private gameService: GameService
  ) {}

  private disposers: IReactionDisposer[] = [];
  private _queue?: SessionQueue;
  private queueRunner = new SyncQueueRunner();

  @observable
  public lastGameFinished = +new Date();

  public async serviceWillMount() {
    try {
      this._initializeActiveSession();
    } catch (e) {
      this.logger.captureException(e, "error");
    }
  }

  public async serviceDidBecomeForeground() {
    this.disposers.push(
      reaction(
        () => [this.reachabilityService.reachable],
        (value) => {
          if (value[0]) this.postQueue();
        },
        { fireImmediately: true }
      )
    );
  }

  public serviceDidBecomeBackground() {
    for (const disposer of this.disposers) disposer();
  }

  public SESSION_ID_KEY = "@gamesession/id";
  public SESSION_GAME_KEY = "@gamesession/game";
  public SESSION_DATA_KEY = "@gamesession/data";
  public VARIABLE_DATA_KEY = "@variables/data";
  public USER_KEY = "USER_KEY";

  public generateSessionId() {
    return generateRandomId("sess");
  }

  @computed
  public get hasActiveSession() {
    return !!this.activeSession;
  }
  @observable
  public activeSession?: GameSessionModel = undefined;

  @observable
  private _previousActiveSession?: GameSessionModel;

  /**
   * This property is meant for use in components that display active session,
   * it holds previous active session if there was any,
   * so that elements do not disappear during pop animations.
   */
  @computed
  public get activeDisplaySession() {
    return this.activeSession || this._previousActiveSession;
  }

  @action
  public setActiveSession(
    gameModel?: GameSessionModel,
    doNotSyncToRemote?: boolean
  ) {
    if (this.activeSession) this._previousActiveSession = this.activeSession;

    // This makes sure listeners are attached to a game model until a new
    // session becomes active
    this._detachGameListeners();
    this.activeSession = gameModel;

    if (gameModel) {
      this._attachGameListeners(gameModel, doNotSyncToRemote);
    }

    Environment.Sentry?.setContext("session", {
      sessionId: this.activeSession?.session.id,
      gameName: this.activeSession?.name,
      release: this.activeSession?.release.releaseVersion,
    });

    Environment.Sentry?.setTag("game_id", this.activeSession?.id || "none");
  }

  private _initializeActiveSession() {
    const activeSessionId = this.getActiveSessionId();
    // Try to restart active session if any
    if (activeSessionId) {
      const model = this.getSessionModel(activeSessionId);
      if (model) this.setActiveSession(model);
    }
  }

  /**
   * Returns `true` when the given session was built from a release older
   * than `latestReleaseVersion`. Used to decide whether to discard a stored
   * session when the division opts into always showing the latest story
   * version.
   */
  public isSessionOutdated(
    session: GameSessionModel,
    latestReleaseVersion?: number
  ): boolean {
    if (latestReleaseVersion === undefined) return false;
    return session.session.releaseVersion < latestReleaseVersion;
  }

  public fetchActiveSession(
    gameId: string,
    options?: FetchActiveSessionOptions
  ) {
    const activeGameSession = this.getGameSessionId(`${gameId}`);
    // Try to restart active session if any
    if (activeGameSession) {
      const model = this.getSessionModel(activeGameSession);
      if (model) {
        // If the division opted into always showing the latest story version,
        // discard the stored session when its release is older than the
        // latest published release so a fresh session is started instead.
        if (
          options?.restartIfOutdated &&
          this.isSessionOutdated(model, options.latestReleaseVersion)
        ) {
          this.clearGameSessionDataForGame(gameId);
          return undefined;
        }
        return model;
      }
    }

    return undefined;
  }

  /**
   * Start a new session of continue a new session
   */
  public continueSession(model: GameSessionModel, divisionId?: string) {
    // NOTE: We reset the download state manually when continueing session,
    // because files may have gone missing during the lifetime of this application
    // run. It is unlikely that this happens, but better safe than sorry.
    model.resetDownloadStates();

    this.setActiveSessionId(model.session.id);

    runInAction(() => {
      if (divisionId)
        model.divisionVariables = this.getDivisionVariables(divisionId);
    });

    return this.setActiveSession(model);
  }

  public async startNewSession(
    game: StartGameEntity,
    metadata?: SessionStartOptions
  ) {
    // Else no session yet or game model was missing, so start a new session
    const sessionId = this.generateSessionId();
    const model = this.initGameSessionModel(
      await this.buildGameSessionModelData(sessionId, game, metadata)
    );

    this.setActiveSessionId(sessionId);
    this.setGameSessionId(game.id, sessionId);
    this.saveSessionData(sessionId, model.toJSON());

    runInAction(() => {
      // save new session to the game entity
      game.activeSession = model;
    });

    this.setActiveSession(model);

    return model;
  }

  public destroyGameSession(game: GameSessionModel) {
    // Else no session yet or game model was missing, so start a new session
    this.clearGameSessionId(game.id);

    delete this.sessionModelCache[game.session.id];
  }

  public getUserKey = () => {
    const existing = this.storageService.getItem(this.USER_KEY);

    if (existing) return existing;

    const userKey = generateRandomId(undefined, 32);
    this.storageService.setItem(this.USER_KEY, userKey);

    return userKey;
  };

  /**
   * Clears active session, and resets the router as a side effect.
   * Avoid using when finishing a session for a user (e.g. when a game
   * has finished). Use FlavorService.finishActiveSession() instead
   */
  public clearActiveSession = () => {
    this.clearActiveSessionId();
    this.setActiveSession(undefined);
  };

  public async buildGameSessionModelData(
    sessionId: string,
    game: StartGameEntity,
    options?: SessionStartOptions
  ) {
    const { id: gameId, release, distanceInMeters, hideLeaderboard } = game;
    if (!gameId || !release.releaseVersion)
      throw new Error(
        "Need game ID, gameinfo and release version to get a new game model"
      );

    const gameInfo: GameInfoData = {
      meters: distanceInMeters || 0,
    };

    const lang = options?.lang || this.i18nService.savedPreferredLanguage;
    const prefLocale =
      this.gameService.resolvePreferredLanguage(release, lang) || lang;

    const bundle = this.gameService.resolveBundleFile(release, lang);

    let gameData: string;
    try {
      gameData = await this.gameService.downloadGameFile(release, bundle);
    } catch (e) {
      // Fallback in case something with base game json went wrong
      gameData = await this.gameService.downloadGameFile(release, "game.json");
    }

    const gameModel: GameSessionModelData = JSON.parse(gameData);

    if (options?.divisionId) {
      gameModel.divisionVariables = this.getDivisionVariables(
        options.divisionId
      );
    }

    if (options?.variables && Object.keys(options.variables).length) {
      gameModel.variables = options.variables;
    }

    if (!gameModel)
      throw new Error("Parsing of gamemodel string resulted in empty model.");

    gameModel.release.languages = game.release.languages;

    gameModel.session = {
      id: sessionId,
      key: generateRandomId(undefined, 32),
      userKey: this.getUserKey(),
      started: new Date(),
      batteryStarted: await this.deviceService.getBatteryLevel(),
      appVersion: Environment.version,
      releaseVersion: release.releaseVersion,
      buildNumber: Environment.buildNumber,
      bundleId: Environment.identifier,
      gameInfo,
      hideLeaderboard,
      locale: prefLocale,
      forcedLocale: options?.lang,
      divisionId: options?.divisionId,
      qrCodeHit: options?.qrCodeHit
        ? {
            id: options?.qrCodeHit,
          }
        : undefined,
      source: options?.source,
      incomingRef: options?.incomingRef,
    };

    return gameModel;
  }

  private getGameSessionId(gameId: string) {
    return this.storageService.getItem(`${this.SESSION_GAME_KEY}/${gameId}`);
  }

  public clearGameSessionId(gameId: string) {
    return this.storageService.removeItem(`${this.SESSION_GAME_KEY}/${gameId}`);
  }

  public clearGameSessionData(sessionId: string) {
    return this.storageService.removeItem(
      `${this.SESSION_DATA_KEY}/${sessionId}`
    );
  }
  public async clearGameSessionDataForGame(gameId: string) {
    const session = this.getGameSessionId(gameId);

    if (session) {
      this.clearGameSessionId(gameId);
      this.clearGameSessionData(session);
    }
    return session;
  }

  private setGameSessionId(gameId: string, sessionId: string): void {
    return this.storageService.setItem(
      `${this.SESSION_GAME_KEY}/${gameId}`,
      sessionId
    );
  }

  private getActiveSessionId(): string | null {
    return this.storageService.getItem(this.SESSION_ID_KEY);
  }

  @action
  private clearActiveSessionId = () => {
    this.lastGameFinished = +new Date();
    return this.storageService.removeItem(this.SESSION_ID_KEY);
  };

  private setActiveSessionId(sessionId: string): void {
    return this.storageService.setItem(this.SESSION_ID_KEY, sessionId);
  }

  /**
   * --- Section variable sync
   */

  public getDivisionVariables(divisionId: string): { [key: string]: string } {
    const data = this.storageService.getItem(
      `${this.VARIABLE_DATA_KEY}/${divisionId}`
    );

    if (!data) return {};
    return JSON.parse(data);
  }

  public syncDivisionVariables(
    divisionId: string,
    variables: { [key: string]: string } | undefined
  ) {
    const data = this.storageService.getItem(
      `${this.VARIABLE_DATA_KEY}/${divisionId}`
    );

    const existing = data ? JSON.parse(data) : {};

    this.storageService.setItem(
      `${this.VARIABLE_DATA_KEY}/${divisionId}`,
      JSON.stringify({
        ...existing,
        ...variables,
      })
    );
  }

  private sessionModelCache: { [key: string]: GameSessionModel } = {};

  public initGameSessionModel(gameModelData: GameSessionModelData) {
    const model = new GameSessionModel(gameModelData, this.fileService);
    // Cache object so that system only ever returns one and the same model per session
    this.sessionModelCache[model.session.id] = model;
    return model;
  }

  public getSessionModel(sessionId: string): GameSessionModel | null {
    const existing = this.sessionModelCache[sessionId];
    if (existing) return existing;

    const data = this.storageService.getItem(
      `${this.SESSION_DATA_KEY}/${sessionId}`
    );
    if (!data) return null;
    const model = this.initGameSessionModel(JSON.parse(data));
    return model;
  }

  public saveSessionData(sessionId: string, game: GameSessionModelData): void {
    return this.storageService.setItem(
      `${this.SESSION_DATA_KEY}/${sessionId}`,
      JSON.stringify(game)
    );
  }

  private get playerName() {
    const user = this.userService.user;
    if (user) {
      return undefined;
    } else {
      return this.userService.nickname.value || undefined;
    }
  }

  /**
   * Sync session and save synced values before trying to send to server
   */
  public sync(data: SessionUpdateSync) {
    const { id } = data;
    const queue = this.getQueue();

    const prevItem = queue[id] || { data: {} };

    const newItem = {
      syncId: generateRandomId(),
      update: {
        playerName: this.playerName,
        ...prevItem.update,
        ...data,
      },
    };

    queue[id] = newItem;
    this.logger.dev(queue[id], "synced game session");

    this.saveQueue();
    this.postQueue();
  }

  private saveQueue() {
    const queue = this.getQueue();
    return this.storageService.setItem(`_queue`, JSON.stringify(queue));
  }

  private getQueue(): SessionQueue {
    if (!this._queue) {
      const queue = this.storageService.getItem(`_queue`);
      this._queue = queue ? JSON.parse(queue)! : {};
    }
    return this._queue!;
  }

  /**
   * Sends the queue now with keepalive, next to any pass already running: on
   * page leave, waiting on an in-flight request would lose the sync.
   */
  public processQueueImmediate() {
    this.postQueue.cancel();
    return this._postQueue(true);
  }

  private postQueue = debounce(() => {
    this.queueRunner.run(() => this._postQueue(false));
  }, 100);

  private _postQueue = async (keepalive: boolean) => {
    const queue = this.getQueue();
    const syncables = Object.values(queue);

    if (!syncables.length) {
      return;
    }

    const post = async ({ syncId, update }: SessionQueueItem) => {
      try {
        await this.send(update, keepalive);
        this.deleteUpdateById(update.id, syncId);
      } catch (e) {
        // 500 errors are getting logged by the server
        if (e.response && e.response.status >= 400 && e.response.status < 500) {
          this.deleteUpdateById(update.id, syncId);
        }
        this.logger.dev(e, "caputered sync error");
      }
    };

    // On page leave every request has to be issued before the page goes, so
    // an abandoned session in the queue must not hold up the active one.
    if (keepalive) {
      await Promise.all(syncables.map(post));
      return;
    }
    for (const item of syncables) await post(item);
  };

  private async send(update: SessionUpdateSync, keepalive: boolean) {
    const data = this.getUpdateData(update);
    if (!keepalive) {
      await this.apiService.clientPublic.updateSession(data);
      return;
    }

    const response = await fetchKeepalive(
      `${this.apiService.basePath}/client/session`,
      {
        method: "PATCH",
        json: JSON.stringify(data),
        headers: this.apiService.requestHeaders,
        keepalive: true,
      }
    );

    if (!response.ok) {
      // Same shape as an axios error, so the caller's 4xx handling applies
      throw Object.assign(new Error(`Sync failed: ${response.status}`), {
        response: { status: response.status },
      });
    }
  }

  private getUpdateData(update: SessionUpdateSync) {
    return {
      ...update,
      ...this.deviceConstants,
      started: update.started.toString(),
      finished: update.finished?.toString(),
    };
  }

  /** Only drops the item when it is still the one that was sent. */
  private deleteUpdateById(id: string, syncId: string) {
    const queue = this.getQueue();
    if (queue[id]?.syncId !== syncId) return;
    delete queue[id];
    this.saveQueue();
  }

  public directUpdate = async (update: SessionUpdateSync) => {
    await this.apiService.clientPublic.updateSession(
      this.getUpdateData(update)
    );
  };

  private _gameEventDisposers: IReactionDisposer[] = [];

  private _attachGameListeners(
    game: GameSessionModel,
    doNotSyncToRemote?: boolean
  ) {
    // Save session data whenever any of the following observables change
    this._gameEventDisposers.push(
      reaction(
        () => [
          game.totalPoints,
          game.baseScenario.activeScene,
          game.finished,
          game.allSceneStates,
          game.reviewDone,
          game.variables,
          game.session.locale,
          game.session.forcedLocale,
          game.session.shareRef,
        ],
        async () => {
          // Set finished time when user got to finish
          await game.checkGameFinished(this.deviceService.getBatteryLevel);
          this.saveSessionData(game.session.id, game.toJSON());
        }
      )
    );

    this._gameEventDisposers.push(
      reaction(
        () => [game.variables],
        async () => {
          if (game.session.divisionId)
            this.syncDivisionVariables(game.session.divisionId, game.variables);
        }
      )
    );

    this._gameEventDisposers.push(
      reaction(
        () => [
          game.completionPercentage,
          game.allCollectedEvents,
          game.session.shareRef,
        ],
        async () => {
          if (!game.session || doNotSyncToRemote) return;
          // Set finished time when user got to finish
          await game.checkGameFinished(this.deviceService.getBatteryLevel);

          // Sync session to the backend server. Activity metrics ride along
          // so gameplay progress and engagement are always kept together.
          this.sync(this.buildSyncPayload(game));
        },
        { fireImmediately: true }
      )
    );

    // Activity metrics are updated on every user interaction by the
    // platform-specific meter. Keying the reaction off the engaged-time
    // bucket throttles the work to roughly once per SESSION_METRICS_SYNC_STEP
    // seconds of active time, without firing while the session is idle. We
    // persist locally (so a reload resumes the metrics) and sync through the
    // queue (which serialises the updates per session).
    this._gameEventDisposers.push(
      reaction(
        () => Math.floor(game.activeSeconds / SESSION_METRICS_SYNC_STEP),
        () => {
          if (!game.session) return;

          this.saveSessionData(game.session.id, game.toJSON());

          if (doNotSyncToRemote) return;
          this.sync({
            ...game.session,
            gameId: game.id,
            activeSeconds: game.activeSeconds,
            actionCount: game.actionCount,
          });
        }
      )
    );
  }

  /**
   * Full snapshot of the session. Stamped before serialising, so the stamp the
   * server orders by is the one inside gameData too. Partial updates (metrics,
   * leaderboard name) reuse the last stamp and never outrank a snapshot.
   */
  private buildSyncPayload(
    game: GameSessionModel
  ): SessionUpdateSync & { gameData: GameSessionModelData } {
    game.session.clientSyncedAt = nextSyncStamp(game.session.clientSyncedAt);
    return {
      ...game.session,
      gameId: game.id,
      gameData: game.toJSON(),
      score: game.totalPoints,
      completion: Math.round(game.completionPercentage),
      activeSceneIndex: game.activeSceneIndex,
      numScenes: game.numScenes,
      variables: game.variables,
      activeSeconds: game.activeSeconds,
      actionCount: game.actionCount,
    };
  }

  /**
   * Persists the active session and sends its full state right away, ahead
   * of any pass already in flight. Called on page leave and backgrounding.
   * Best effort: failures are only logged.
   */
  public flushActiveSession = () => {
    const game = this.activeSession;
    if (!game?.session) return;

    try {
      const payload = this.buildSyncPayload(game);
      this.saveSessionData(game.session.id, payload.gameData);
      this.sync(payload);
      this.processQueueImmediate();
    } catch (e) {
      this.logger.dev(e, "session flush failed");
    }
  };

  private _detachGameListeners() {
    this._gameEventDisposers.forEach((dispose) => dispose());
  }

  public modelForGame(game: GameEntity, sessions: SessionEntity[]) {
    const sessionId = this.getGameSessionId(game.id);
    const gameModel = sessionId ? this.getSessionModel(sessionId) : null;
    if (gameModel) return gameModel;

    const remoteSession = sessions.find(
      (session) => session.game?.id === game.id
    );

    if (remoteSession?.gameData) {
      // If remote session is matching, save the GameSessionModel locally
      const gameData = remoteSession.gameData as GameSessionModelData;

      this.saveSessionData(remoteSession.id, gameData);

      remoteSession.id, this.setGameSessionId(gameData.id, remoteSession.id);

      return this.initGameSessionModel(gameData);
    }

    return undefined;
  }

  public checkActiveLanguage = async (
    game: GameSessionModel,
    forceLang?: string
  ) => {
    const { savedPreferredLanguage } = this.i18nService;

    const forcedPref =
      forceLang || game.session.forcedLocale || savedPreferredLanguage;

    if (forcedPref && forcedPref === game.session.locale) {
      return;
    }

    const availableLanguage = this.gameService.resolvePreferredLanguage(
      game.release,
      forcedPref
    );

    if (!availableLanguage || availableLanguage === game.session.locale) return;

    const gameData = await this.buildGameSessionModelData(
      game.session.id,
      game,
      {
        lang: availableLanguage,
      }
    );

    runInAction(() => {
      game.mergeWithData(gameData);
      game.session.locale = availableLanguage;
      if (forceLang) game.session.forcedLocale = forceLang;
    });

    this.saveSessionData(game.session.id, game.toJSON());
  };
}
